import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, FileText, UserCog, Users, CheckCircle2, BarChart3,
    GitCompare, Radar, MapPin, ArrowLeft, Landmark, MoreHorizontal, X, Info,
} from "lucide-react";
import { useFeatureFlags } from "../FeatureFlagsContext";
import { getJwtRole } from "../../service/AuthService";
import { admin, FEATURES } from "../../constants/Constants";

// All nav items, grouped. Each carries its own feature flag. Exported so the
// funder landing page (/funders) can list the same pages it advertises.
export const FUNDER_NAV = [
    { group: "workflow", label: "Call Overview", mobileLabel: "Overview", icon: LayoutDashboard, path: "/funder", flag: FEATURES.FUNDER_CALL_OVERVIEW },
    { group: "workflow", label: "Submissions", mobileLabel: "Subs", icon: FileText, path: "/funder/submissions", flag: FEATURES.FUNDER_SUBMISSIONS },
    { group: "workflow", label: "Reviewer Management", mobileLabel: "Reviewers", icon: UserCog, path: "/funder/reviewer-management", flag: FEATURES.FUNDER_REVIEWER_MANAGEMENT },
    { group: "workflow", label: "Committee", mobileLabel: "Committee", icon: Users, path: "/funder/committee", flag: FEATURES.FUNDER_COMMITTEE },
    { group: "workflow", label: "Decisions & Feedback", mobileLabel: "Decisions", icon: CheckCircle2, path: "/funder/decisions", flag: FEATURES.FUNDER_DECISIONS },
    { group: "strategic", label: "Portfolio Overview", mobileLabel: "Portfolio", icon: BarChart3, path: "/funder/portfolio", flag: FEATURES.FUNDER_PORTFOLIO },
    { group: "strategic", label: "Funder Comparison", mobileLabel: "Compare", icon: GitCompare, path: "/funder/comparison", flag: FEATURES.FUNDER_COMPARISON },
    { group: "strategic", label: "Preprint Radar", mobileLabel: "Preprints", icon: Radar, path: "/funder/preprints", flag: FEATURES.FUNDER_PREPRINTS },
    { group: "strategic", label: "White Space", mobileLabel: "Whitespace", icon: MapPin, path: "/funder/white-space", flag: FEATURES.FUNDER_WHITE_SPACE },
];

export default function FunderSidebar({ activePage = "Call Overview" }) {
    const navigate = useNavigate();
    const { isEnabled } = useFeatureFlags();
    const isAdmin = getJwtRole() === admin;
    const [moreOpen, setMoreOpen] = useState(false);

    const masterOn = isEnabled(FEATURES.FUNDER_PORTAL);
    // Visible to the current user: admins always see every item (so they can
    // verify disabled pages); everyone else only sees live ones.
    const isVisible = (item) => isAdmin || (masterOn && isEnabled(item.flag));
    // Shown to an admin but currently disabled for regular users.
    const isOff = (item) => !masterOn || !isEnabled(item.flag);

    const items = FUNDER_NAV.filter(isVisible);
    const go = (path) => { setMoreOpen(false); navigate(path); };

    const renderNavItem = (item) => {
        const Icon = item.icon;
        const off = isOff(item);
        return (
            <div
                key={item.label}
                className={`nav-item${item.label === activePage ? " nav-item-active" : ""}`}
                onClick={() => go(item.path)}
                title={off ? "Hidden from users (disabled)" : undefined}
                style={off ? { opacity: 0.55 } : undefined}
            >
                <Icon size={14} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {off && <span title="Hidden from users" style={{ width: 7, height: 7, borderRadius: "50%", background: "#d4a017", flexShrink: 0 }} />}
            </div>
        );
    };

    const workflow = items.filter((i) => i.group === "workflow");
    const strategic = items.filter((i) => i.group === "strategic");

    // Mobile bottom bar: first 4 visible items + a "More" sheet for the rest.
    const mobilePrimary = items.slice(0, 4);
    const mobileRest = items.slice(4);

    return (
        <>
            <aside className="sidebar">
                <div className="sidebar-logo" onClick={() => navigate("/funder")} style={{ cursor: "pointer" }}>
                    <div className="sidebar-logo-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Landmark size={20} color="var(--brand-text)" />
                    </div>
                    <span className="sidebar-logo-name">Funder Portal</span>
                </div>

                <nav className="sidebar-nav">
                    {workflow.map(renderNavItem)}
                    {strategic.length > 0 && <div className="funder-nav-section">Strategic</div>}
                    {strategic.map(renderNavItem)}
                </nav>

                <div className="sidebar-bottom">
                    <div className="nav-item" onClick={() => navigate("/funders")}>
                        <Info size={14} />
                        About the Platform
                    </div>
                    <div className="nav-item" onClick={() => navigate("/")}>
                        <ArrowLeft size={14} />
                        Back to DeepThought
                    </div>
                </div>
            </aside>

            <nav className="mobile-nav">
                {mobilePrimary.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.label === activePage;
                    return (
                        <button
                            key={item.label}
                            className={`mobile-nav-item${isActive ? " mobile-nav-item-active" : ""}`}
                            onClick={() => go(item.path)}
                        >
                            <Icon size={22} />
                            <span className="mobile-nav-label">{item.mobileLabel}</span>
                        </button>
                    );
                })}
                <button
                    className={`mobile-nav-item${moreOpen ? " mobile-nav-item-active" : ""}`}
                    onClick={() => setMoreOpen((o) => !o)}
                >
                    <MoreHorizontal size={22} />
                    <span className="mobile-nav-label">More</span>
                </button>
            </nav>

            {moreOpen && (
                <>
                    <div className="funder-more-backdrop" onClick={() => setMoreOpen(false)} />
                    <div className="funder-more-sheet" role="menu">
                        <div className="funder-more-header">
                            <span>Funder Portal</span>
                            <button className="funder-more-close" onClick={() => setMoreOpen(false)} aria-label="Close menu"><X size={18} /></button>
                        </div>
                        {mobileRest.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button key={item.label} className="funder-more-item" onClick={() => go(item.path)}>
                                    <Icon size={16} />
                                    <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
                                    {isOff(item) && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#d4a017" }} />}
                                </button>
                            );
                        })}
                        <button className="funder-more-item" onClick={() => go("/funders")}>
                            <Info size={16} />
                            <span style={{ flex: 1, textAlign: "left" }}>About the Platform</span>
                        </button>
                        <button className="funder-more-item" onClick={() => go("/")}>
                            <ArrowLeft size={16} />
                            <span style={{ flex: 1, textAlign: "left" }}>Back to DeepThought</span>
                        </button>
                    </div>
                </>
            )}
        </>
    );
}
